
import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabaseClient';
import { Trophy, Globe, Lock, ArrowRight, PlusCircle, ShieldCheck, Users } from 'lucide-react';
import logoSavat from '../assets/logo_savatevent.jpg';

export default function HomeDashboard({ user, onSelect, onNavigate, onShowLogin }) {
    const [myRecent, setMyRecent] = useState([]);   // propriétaire
    const [sharedRecent, setSharedRecent] = useState([]); // co-owner / co-manager
    const [publicRecent, setPublicRecent] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchData();
    }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchData = async () => {
        setLoading(true);
        try {
            const pubPromise = supabase.from('competitions')
                .select('*')
                .eq('visibility', 'public')
                .order('created_at', { ascending: false })
                .limit(6);

            if (user) {
                const [ownedRes, asMgrRes, asOwnerRes, pubRes] = await Promise.all([
                    supabase.from('competitions').select('*').eq('owner_id', user.id).order('created_at', { ascending: false }).limit(6),
                    supabase.from('competitions').select('*').contains('co_managers', [user.email]).order('created_at', { ascending: false }).limit(6),
                    supabase.from('competitions').select('*').contains('co_owners', [user.email]).order('created_at', { ascending: false }).limit(6),
                    pubPromise
                ]);
                setMyRecent(ownedRes.data || []);

                // Fusion co-owner + co-manager sans doublons, avec rôle annoté
                const ownedIds = new Set((ownedRes.data || []).map(c => c.id));
                const coOwnerComps = (asOwnerRes.data || []).filter(c => !ownedIds.has(c.id)).map(c => ({ ...c, _role: 'co_owner' }));
                const coMgrIds = new Set(coOwnerComps.map(c => c.id));
                const coMgrComps = (asMgrRes.data || []).filter(c => !ownedIds.has(c.id) && !coMgrIds.has(c.id)).map(c => ({ ...c, _role: 'co_manager' }));
                setSharedRecent([...coOwnerComps, ...coMgrComps]);

                setPublicRecent(pubRes.data || []);
            } else {
                const pubRes = await pubPromise;
                setPublicRecent(pubRes.data || []);
            }
        } catch (error) {
            console.error("Erreur HomeDashboard:", error);
        } finally {
            setLoading(false);
        }
    };

    const CompetitionCard = ({ comp, isPublic }) => {
        const role = comp._role; // 'co_owner' | 'co_manager' | undefined
        const borderColor = role === 'co_owner' ? '#d6bcfa' : role === 'co_manager' ? '#bee3f8' : '#e2e8f0';
        return (
            <div
                onClick={() => onSelect(comp)}
                style={{
                    padding: '20px', backgroundColor: 'white', borderRadius: '12px',
                    cursor: 'pointer', border: `1px solid ${borderColor}`, transition: 'all 0.2s',
                    display: 'flex', flexDirection: 'column', gap: '12px'
                }}
                onMouseOver={(e) => { e.currentTarget.style.borderColor = '#3182ce'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseOut={(e) => { e.currentTarget.style.borderColor = borderColor; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
                {role === 'co_owner' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', fontWeight: '700', color: '#7c3aed', textTransform: 'uppercase' }}>
                        <ShieldCheck size={12} /> Co-propriétaire
                    </div>
                )}
                {role === 'co_manager' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', fontWeight: '700', color: '#2b6cb0', textTransform: 'uppercase' }}>
                        <Users size={12} /> Co-organisateur
                    </div>
                )}
                <h4 style={{ margin: 0, fontSize: '16px', color: '#2d3748' }}>{comp.name}</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    <div style={{ fontSize: '13px', color: '#4a5568', display: 'flex', alignItems: 'center', gap: '5px' }}>
                        📍 {comp.address || "Lieu non précisé"}
                    </div>
                    <div style={{ fontSize: '13px', color: '#4a5568', display: 'flex', alignItems: 'center', gap: '5px' }}>
                        📅 {comp.event_date ? new Date(comp.event_date).toLocaleDateString('fr-FR') : "Date non précisée"}
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#718096', borderTop: '1px solid #edf2f7', paddingTop: '10px', marginTop: '5px' }}>
                    {comp.visibility === 'public' ? <Globe size={14} color="#38a169"/> : <Lock size={14}/>}
                    <span>{isPublic ? "Suivre le direct" : "Gérer le plateau →"}</span>
                </div>
            </div>
        );
    };

    if (loading) return <div style={{ padding: '40px', textAlign: 'center' }}>Chargement...</div>;

    return (
        <div style={{ padding: '20px', maxWidth: '1000px', margin: '0 auto' }}>
            <div style={{ marginBottom: '32px', display: 'flex', alignItems: 'center', gap: '20px' }}>
                <img src={logoSavat} alt="Logo" style={{ width: '80px', height: '80px', borderRadius: '15px', objectFit: 'cover', border: '3px solid #3182ce' }} />
                <div>
                    <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1a202c', margin: 0 }}>Bienvenue sur SavatEvent</h1>
                    <p style={{ color: '#718096', margin: '5px 0 0 0' }}>Organisez et suivez vos compétitions de Savate en temps réel.</p>
                </div>
            </div>

            {/* SECTION MES COMPÉTITIONS */}
            <div style={{ marginBottom: '40px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h2 style={{ fontSize: '20px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Trophy size={20} color="#3182ce" /> Mes Compétitions Récentes
                    </h2>
                    <button 
                        onClick={() => onNavigate('list_competitions')}
                        style={{ background: 'none', border: 'none', color: '#3182ce', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', fontSize: '14px', fontWeight: '600' }}
                    >
                        Voir tout <ArrowRight size={14} />
                    </button>
                </div>

                {!user ? (
                    <div style={{ backgroundColor: '#ebf8ff', padding: '20px', borderRadius: '12px', textAlign: 'center', border: '1px solid #bee3f8' }}>
                        <p style={{ color: '#2c5282', marginBottom: '15px' }}>Connectez-vous pour créer et gérer vos propres compétitions.</p>
                        <button 
                            onClick={onShowLogin}
                            style={{ backgroundColor: '#3182ce', color: 'white', padding: '8px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: 'bold' }}
                        >
                            Se connecter
                        </button>
                    </div>
                ) : (
                    <>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
                            {/* Bouton Créer Rapide */}
                            <div 
                                onClick={() => onNavigate('list_competitions')}
                                style={{ 
                                    padding: '20px', backgroundColor: '#f0fff4', borderRadius: '12px', 
                                    cursor: 'pointer', border: '2px dashed #38a169', transition: 'all 0.2s',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                                    color: '#2f855a', fontWeight: 'bold'
                                }}
                            >
                                <PlusCircle size={24} /> Créer une compétition
                            </div>
                            {myRecent.map(comp => <CompetitionCard key={comp.id} comp={comp} isPublic={false} />)}
                        </div>
                        {myRecent.length === 0 && (
                            <p style={{ textAlign: 'center', color: '#a0aec0', marginTop: '20px' }}>Vous n'avez pas encore créé de compétition.</p>
                        )}

                        {sharedRecent.length > 0 && (
                            <>
                                <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#4a5568', margin: '24px 0 12px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Users size={16} color="#718096" /> Partagées avec moi
                                </h3>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
                                    {sharedRecent.map(comp => <CompetitionCard key={comp.id} comp={comp} isPublic={false} />)}
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>

            {/* SECTION COMPÉTITIONS PUBLIQUES */}
            <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h2 style={{ fontSize: '20px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Globe size={20} color="#38a169" /> Compétitions Publiques Récentes
                    </h2>
                    <button 
                        onClick={() => onNavigate('public_list')}
                        style={{ background: 'none', border: 'none', color: '#3182ce', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', fontSize: '14px', fontWeight: '600' }}
                    >
                        Voir tout <ArrowRight size={14} />
                    </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
                    {publicRecent.map(comp => <CompetitionCard key={comp.id} comp={comp} isPublic={true} />)}
                    {publicRecent.length === 0 && (
                        <p style={{ textAlign: 'center', color: '#a0aec0', gridColumn: '1 / -1' }}>Aucune compétition publique en cours.</p>
                    )}
                </div>
            </div>
        </div>
    );
}
